/**
 * useMasterDataContacts Hook
 * ahooks data fetching hook for master data contact list with filters, pagination, and sorting
 */

import { useRequest, useAntdTable, clearCache } from 'ahooks';
import type { Options } from 'ahooks/lib/useRequest/src/types';
import { trackCacheKey, clearCacheWhere } from '@/utils/cacheKeyRegistry';
import {
  useMasterDataContactStore,
  useMasterDataContactQueryParams,
} from '@/stores/masterDataContactStore';
import { masterDataContactApi } from '@/services/api/masterDataContact.api';
import type {
  MasterDataContactListResponse,
  MasterDataContactListParams,
  MasterDataContactDetail,
  MasterDataContactSortField,
  ContactType,
  VendorListItem,
  CustomerListItem,
} from '@/types/api/masterDataContact';

/**
 * Hook to fetch master data contact list with automatic state management
 * Syncs with Zustand store for filters, pagination, and sorting
 */
export function useMasterDataContacts(config?: Options<MasterDataContactListResponse, []>) {
  // Get query params from store
  const storeParams = useMasterDataContactQueryParams();
  const { setContacts, setPagination, setLoading, setError } = useMasterDataContactStore();

  // Fetch contacts with ahooks
  const cacheKey = trackCacheKey(`/api/contacts?${JSON.stringify(storeParams)}`);
  const {
    data,
    error,
    loading,
    refresh: _refresh,
    run,
    mutate,
  } = useRequest(() => masterDataContactApi.list(storeParams as MasterDataContactListParams), {
    refreshDeps: [JSON.stringify(storeParams)],
    cacheKey,
    staleTime: 60000, // 1 minute
    cacheTime: 60000, // Cache expiration
    ...config,
    onSuccess: (data) => {
      setContacts(data.data);
      setPagination(data.pagination);
      setLoading(false);
      setError(null);
      config?.onSuccess?.(data, []);
    },
    onError: (err) => {
      setLoading(false);
      setError(err.message || 'Failed to fetch contacts');
      config?.onError?.(err, []);
    },
  });

  return {
    contacts: data?.data ?? [],
    pagination: data?.pagination,
    error,
    isLoading: loading,
    mutate,
    refresh: () => {
      // Clear all contact-related caches
      clearCache(cacheKey);
      clearCacheWhere((key) => key.startsWith('/api/contacts'));
      // Force refresh by running the request again
      return run();
    },
  };
}

/**
 * Hook using useAntdTable for seamless Ant Design Table integration
 * Returns tableProps that can be spread directly onto <Table>
 *
 * @param filters - External filters (search, typeId, isPrimary, isActive) from store or local state
 */
export function useMasterDataContactsTable(filters: {
  search?: string;
  typeId: ContactType;
  isPrimary?: boolean | null;
  isActive?: boolean | null;
}) {
  const { setContacts, setPagination } = useMasterDataContactStore();

  // Convert filters to API format
  const getApiParams = (filters: {
    search?: string;
    typeId: ContactType;
    isPrimary?: boolean | null;
    isActive?: boolean | null;
  }): Partial<MasterDataContactListParams> => {
    // Convert isPrimary boolean to API format: 1=primary, 2=general, 3=all
    let isPrimaryParam: number | undefined;
    if (filters.isPrimary === true) {
      isPrimaryParam = 1;
    } else if (filters.isPrimary === false) {
      isPrimaryParam = 2;
    } else {
      isPrimaryParam = undefined; // null means all (don't send param)
    }

    // Convert isActive boolean to API format: 1=active, 2=inactive, 3=all
    let isActiveParam: number | undefined;
    if (filters.isActive === true) {
      isActiveParam = 1;
    } else if (filters.isActive === false) {
      isActiveParam = 2;
    } else {
      isActiveParam = undefined; // null means all (don't send param)
    }

    return {
      search: filters.search || undefined,
      typeId: filters.typeId,
      isPrimary: isPrimaryParam,
      isActive: isActiveParam,
    };
  };

  const { tableProps, refresh, loading, error, params } = useAntdTable(
    async ({ current, pageSize, sorter }) => {
      // Map sorter to API format
      let sortBy: MasterDataContactSortField | undefined;
      let sortOrder: 0 | 1 | undefined;

      if (sorter && 'field' in sorter && sorter.order) {
        // API sort_by: 1=name, 2=jobTitle, 3=companyName, 4=phone, 5=email, 6=isPrimary, 7=isActive
        const fieldToSortBy: Record<string, MasterDataContactSortField> = {
          name: 1,
          jobTitle: 2,
          companyName: 3,
          phoneNumber: 4,
          phone: 4,
          email: 5,
          isPrimary: 6,
          primary: 6,
          isActive: 7,
        };
        sortBy = fieldToSortBy[sorter.field as string];
        sortOrder = sorter.order === 'descend' ? 1 : 0;
      }

      const params: MasterDataContactListParams = {
        page: current,
        pageSize,
        typeId: filters.typeId, // Required field
        ...getApiParams(filters),
        sortBy,
        sortOrder,
      };

      const response = await masterDataContactApi.list(params);

      // Sync with Zustand store
      setContacts(response.data);
      setPagination(response.pagination);

      return {
        total: response.pagination.total,
        list: response.data,
      };
    },
    {
      defaultPageSize: 10,
      refreshDeps: [filters.search, filters.typeId, filters.isPrimary, filters.isActive],
    }
  );

  return {
    tableProps,
    refresh: () => {
      // Clear caches and refresh
      clearCacheWhere((key) => key.startsWith('/api/contacts'));
      refresh();
    },
    isLoading: loading || params.length === 0,
    error,
  };
}

/**
 * Hook to fetch contact detail by ID
 */
export function useMasterDataContactDetail(
  contactId: number | null,
  entityId: number | null, // vendorId or customerId
  type: ContactType | null,
  config?: Options<MasterDataContactDetail, []>
) {
  const cacheKey = trackCacheKey(
    contactId && entityId && type
      ? `/api/contacts/${contactId}?entityId=${entityId}&type=${type}`
      : undefined
  );

  const {
    data,
    error,
    loading,
    refresh: _refresh,
    run,
    mutate,
  } = useRequest(() => masterDataContactApi.getById(contactId!, entityId!, type!), {
    ready: !!contactId && !!entityId && !!type,
    cacheKey,
    staleTime: 30000, // 30 seconds
    ...config,
  });

  return {
    contact: data,
    error,
    isLoading: loading,
    mutate,
    refresh: () => {
      // Clear cache for this specific contact detail
      if (cacheKey) {
        clearCache(cacheKey);
      }
      // Force refresh by running the request again (bypasses cache)
      return run();
    },
  };
}

/**
 * Hook for master data contact mutations (create, update, toggle status, set primary)
 */
export function useMasterDataContactMutations() {
  const { updateContactInList, removeContact } = useMasterDataContactStore();

  const createVendorContact = async (
    vendorId: number,
    data: Parameters<typeof masterDataContactApi.createVendorContact>[1]
  ) => {
    const response = await masterDataContactApi.createVendorContact(vendorId, data);
    // Clear list caches to show new contact
    clearCacheWhere((key) => key.startsWith('/api/contacts'));
    return response;
  };

  const createCustomerContact = async (
    customerId: number,
    data: Parameters<typeof masterDataContactApi.createCustomerContact>[1]
  ) => {
    const response = await masterDataContactApi.createCustomerContact(customerId, data);
    // Clear list caches to show new contact
    clearCacheWhere((key) => key.startsWith('/api/contacts'));
    return response;
  };

  const updateVendorContact = async (
    vendorId: number,
    contactId: number,
    data: Parameters<typeof masterDataContactApi.updateVendorContact>[2]
  ) => {
    const response = await masterDataContactApi.updateVendorContact(vendorId, contactId, data);
    // Clear caches
    clearCacheWhere((key) => key.includes('/api/contacts'));
    return response;
  };

  const updateCustomerContact = async (
    customerId: number,
    contactId: number,
    data: Parameters<typeof masterDataContactApi.updateCustomerContact>[2]
  ) => {
    const response = await masterDataContactApi.updateCustomerContact(customerId, contactId, data);
    // Clear caches
    clearCacheWhere((key) => key.includes('/api/contacts'));
    return response;
  };

  const toggleVendorContactStatus = async (
    vendorId: number,
    contactId: number,
    isActive: boolean
  ) => {
    const response = await masterDataContactApi.toggleVendorContactStatus(
      vendorId,
      contactId,
      isActive
    );
    updateContactInList(contactId, { isActive });
    // Clear caches
    clearCacheWhere((key) => key.includes('/api/contacts'));
    return response;
  };

  const toggleCustomerContactStatus = async (
    customerId: number,
    contactId: number,
    isActive: boolean
  ) => {
    const response = await masterDataContactApi.toggleCustomerContactStatus(
      customerId,
      contactId,
      isActive
    );
    updateContactInList(contactId, { isActive });
    // Clear caches
    clearCacheWhere((key) => key.includes('/api/contacts'));
    return response;
  };

  const setVendorContactPrimary = async (
    vendorId: number,
    contactId: number,
    isPrimary: boolean
  ) => {
    const response = await masterDataContactApi.setVendorContactPrimary(
      vendorId,
      contactId,
      isPrimary
    );
    updateContactInList(contactId, { isPrimary });
    // Clear caches
    clearCacheWhere((key) => key.includes('/api/contacts'));
    return response;
  };

  const setCustomerContactPrimary = async (
    customerId: number,
    contactId: number,
    isPrimary: boolean
  ) => {
    const response = await masterDataContactApi.setCustomerContactPrimary(
      customerId,
      contactId,
      isPrimary
    );
    updateContactInList(contactId, { isPrimary });
    // Clear caches
    clearCacheWhere((key) => key.includes('/api/contacts'));
    return response;
  };

  return {
    createVendorContact,
    createCustomerContact,
    updateVendorContact,
    updateCustomerContact,
    toggleVendorContactStatus,
    toggleCustomerContactStatus,
    setVendorContactPrimary,
    setCustomerContactPrimary,
    removeContact,
  };
}

/**
 * Hook to fetch vendors list for dropdown
 */
export function useVendorsForDropdown(config?: Options<VendorListItem[], []>) {
  const cacheKey = '/api/vendors/dropdown';
  const { data, error, loading } = useRequest(() => masterDataContactApi.getVendorsList(), {
    cacheKey,
    staleTime: 300000, // 5 minutes
    cacheTime: 300000,
    ...config,
  });

  return {
    vendors: data ?? [],
    error,
    isLoading: loading,
  };
}

/**
 * Hook to fetch customers list for dropdown
 */
export function useCustomersForDropdown(config?: Options<CustomerListItem[], []>) {
  const cacheKey = '/api/customers/dropdown';
  const { data, error, loading } = useRequest(() => masterDataContactApi.getCustomersList(), {
    cacheKey,
    staleTime: 300000, // 5 minutes
    cacheTime: 300000,
    ...config,
  });

  return {
    customers: data ?? [],
    error,
    isLoading: loading,
  };
}
